import Request from './request';
import * as query from "@arcgis/core/rest/query";
import Query from "@arcgis/core/rest/support/Query";
import FieldNames from "../defaults/FieldNames.json";
import * as projection from "@arcgis/core/geometry/projection";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import * as geometryJsonUtils from "@arcgis/core/geometry/support/jsonUtils";
import { getAppConfig, getAttributeServiceUrl, getNifcToken } from "../utils/ReduxHelper";
import { getSaveableDate } from "../utils/DateHelper";

const ProjectService = {
  getAllProjects: function() {
    try
    {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const outFields =  `${FieldNames.projectFields.name}, ${FieldNames.projectFields.fireName}, ${FieldNames.projectFields.id}, ${FieldNames.projectFields.wildfireAcres}, ${FieldNames.projectFields.fireCode}, 
        ${FieldNames.projectFields.irwinID}, ${FieldNames.projectFields.fireDiscoveryDate}, ${FieldNames.projectFields.containmentDate}, ${FieldNames.projectFields.ignitionFiscalYear}, 
        ${FieldNames.projectFields.notes}, ${FieldNames.projectFields.entityType}, ${FieldNames.projectFields.isFirePerimeter}`;
      const queryObject = new Query({
        where: `(OBJECTID IS NOT NULL)`,
        outFields: outFields,
        returnDistinctValues: true,
        orderByFields: "Name",
        returnGeometry: false,
      });
      return query.executeQueryJSON(rootServiceUrl + "/" + config.ifprsProjects[config.env].layerId, queryObject).then(results => {
        let items = [];
        if(results && results.features && results.features.length > 0) {
          items = results.features.map((eaItem) => eaItem.attributes);
        } 
        return(items)
      });    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  getIsProjectNameExisting: function(name, objectID){
    //Check with Verify button so keep this comparing the Name field
    try
    {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const outFields =  `${FieldNames.projectFields.name}`;
      let where = "(OBJECTID IS NOT NULL)";
      if(objectID){
        where = `(${FieldNames.projectFields.name} = '${name}' AND ${FieldNames.projectFields.objectID} <> ${objectID})`
      } else {
        where = `(${FieldNames.projectFields.name} = '${name}')`;
      }
      const queryObject = new Query({
        where: where,
        outFields: outFields,
        returnDistinctValues: true,
        orderByFields: "Name",
        returnGeometry: false,
      });
      return query.executeQueryJSON(rootServiceUrl + "/" + config.ifprsProjects[config.env].layerId, queryObject).then(results => {
        let count = 0;
        if(results && results.features && results.features.length > 0) {
          count = results.features.length;
        } 
        return(count)
      });    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  getProjectByObjectID: function(id){
    try
    {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const outFields =  `${FieldNames.projectFields.objectID}, ${FieldNames.projectFields.name}, ${FieldNames.projectFields.fireName}, ${FieldNames.projectFields.id}, ${FieldNames.projectFields.wildfireAcres}, ${FieldNames.projectFields.fireCode}, 
        ${FieldNames.projectFields.irwinID}, ${FieldNames.projectFields.fireDiscoveryDate}, ${FieldNames.projectFields.containmentDate}, ${FieldNames.projectFields.ignitionFiscalYear}, 
        ${FieldNames.projectFields.lastModifiedBy}, ${FieldNames.projectFields.lastModifiedDate}, ${FieldNames.projectFields.createdBy}, ${FieldNames.projectFields.createdOnDate}, 
        ${FieldNames.projectFields.notes}, ${FieldNames.projectFields.entityType}, ${FieldNames.projectFields.isFirePerimeter}`;
      const queryObject = new Query({
        where: `${FieldNames.projectFields.objectID}=${id}`,
        outFields: outFields,
        returnGeometry: true,
      });
      return query.executeQueryJSON(rootServiceUrl + "/" + config.ifprsProjects[config.env].layerId, queryObject).then(results => {
        let items = [];
        if(results && results.features && results.features.length > 0) {
          items = results.features?.[0];
        } 
        return(items)
      }); 
    } catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  getProjectObjectIDByID: function(id){
    try
    {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const outFields =  `${FieldNames.projectFields.objectID}`;
      const queryObject = new Query({
        where: `${FieldNames.projectFields.id}=${id}`,
        outFields: outFields,
        returnGeometry: true,
      });
      return query.executeQueryJSON(rootServiceUrl + "/" + config.ifprsProjects[config.env].layerId, queryObject).then(results => {
        let items = [];
        if(results && results.features && results.features.length > 0) {
          items = results.features?.[0];
        } 
        return(items)
      }); 
    } catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  getProjectByNameIrwinIDFireCode: function(name, irwinID, fireCode){
    //Compare the Project in the Fire Perimeter with the Fire Name field in the projects table
    try
    {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const outFields =  `${FieldNames.projectFields.objectID}`;
      const whereArray = []
      if(name){
        whereArray.push(`${FieldNames.projectFields.fireName} = '${name}'`);
      } else {
        whereArray.push(`${FieldNames.projectFields.fireName} IS NULL`);
      }

      if(irwinID){
        whereArray.push(`${FieldNames.projectFields.irwinID} = '${irwinID}'`);
      } else {
        whereArray.push(`${FieldNames.projectFields.irwinID} IS NULL`);
      }
      
      if(fireCode){
        whereArray.push(`${FieldNames.projectFields.fireCode} = '${fireCode}'`);
      } else {
        whereArray.push(`${FieldNames.projectFields.fireCode} IS NULL`);
      }
      //We should never have all 3 empty
      const whereClause = whereArray?.length > 0 ? whereArray.join(" AND ") : 'OBJECTID IS NOT NULL'
      
      const queryObject = new Query({
        where: whereClause,
        outFields: outFields,
        returnGeometry: false,
      });
      return query.executeQueryJSON(rootServiceUrl + "/" + config.ifprsProjects[config.env].layerId, queryObject).then(results => {
        let items = [];
        if(results && results.features && results.features.length > 0) {
          items = results.features
        } 
        return(items)
      });    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  addProject: function (perimeterJSON, attributes, userInfo) {
    try
    {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const nifcToken = getNifcToken();
      let featureUrl =`${rootServiceUrl}/${config.ifprsProjects[config.env].layerId}/addFeatures`;

      return new Promise((resolve, reject) => {
        projection.load().then(function(){
          const perimeter = geometryJsonUtils.fromJSON(perimeterJSON);
          const nad83geometry = projection.project(perimeter, new SpatialReference({wkid: 4269}));
          let newProj = Object.assign({}, attributes);
          newProj[FieldNames.projectFields.createdBy] = userInfo[FieldNames.userFields.nifcUsername];
          newProj[FieldNames.projectFields.lastModifiedBy] = userInfo[FieldNames.userFields.nifcUsername];
          newProj[FieldNames.projectFields.createdOnDate] = getSaveableDate(new Date());
          newProj[FieldNames.projectFields.lastModifiedDate] = getSaveableDate(new Date());

          const feature =
          {
            geometry: nad83geometry.toJSON(),
            attributes: newProj
          };
  
          new Request().makeRequest({
              url: featureUrl,
              method: 'post',
              data: {
                  f: 'json',
                  token: nifcToken,
                  features: JSON.stringify([feature])
              }
          }).then(
              resp => {
                resolve(resp)
              },
              error => {
                reject(error)
              }
          );
        })
        
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  updateProject: function (perimeterJSON, attributes, userInfo) {
    //Will never get here if project is from Fire Perimeter
    try
    {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const nifcToken = getNifcToken();
      let featureUrl =`${rootServiceUrl}/${config.ifprsProjects[config.env].layerId}/updateFeatures`;

      return new Promise((resolve, reject) => {
        projection.load().then(function(){
          const perimeter = geometryJsonUtils.fromJSON(perimeterJSON);
          const nad83geometry = projection.project(perimeter, new SpatialReference({wkid: 4269}));

          const feature =
          {
            geometry: nad83geometry.toJSON(),
            attributes: attributes
          };
  
          new Request().makeRequest({
              url: featureUrl,
              method: 'post',
              data: {
                  f: 'json',
                  token: nifcToken,
                  features: JSON.stringify([feature])
              }
          }).then(
              resp => {
                resolve(resp)
              },
              error => {
                reject(error)
              }
          );
        })
        
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
};

export default ProjectService;